import { useEffect, useState } from "react"

import evImagesManifest from "../data/ev-images-manifest.json"
import { getCacheBustVersionParam, getDataAssetsUrl } from "../helpers/general"

const assetsUrl = getDataAssetsUrl()
const cacheBustVersion = getCacheBustVersionParam()

const getCurrentSize = () => {
  const width = window.innerWidth
  if (width <= 769) return "S"
  if (width <= 1360) return "M"
  return "L"
}

const preloadImage = src => {
  return new Promise(resolve => {
    const img = new Image()
    img.onload = () => resolve()
    img.onerror = () => resolve()
    img.src = src
  })
}

/**
 * Preloads all images (webp + png) for a given model at the current viewport size.
 * Returns true when all images are cached and ready.
 */
const usePreloadImages = (imagesBasePath, model) => {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!imagesBasePath || !model) {
      setReady(true)
      return
    }

    const upperModel = model.toUpperCase()
    const imagePaths = evImagesManifest[upperModel]

    if (!imagePaths || imagePaths.length === 0) {
      setReady(true)
      return
    }

    let cancelled = false
    const size = getCurrentSize()

    // Filter to only the current viewport size
    const filteredPaths = imagePaths.filter(p => p.includes(`/${size}/`))

    const promises = filteredPaths.flatMap(relativePath => {
      const baseUrl = `${assetsUrl}/${imagesBasePath}/${upperModel}/${relativePath}`
      // The manifest contains .webp paths; also preload the .png equivalent
      const pngPath = relativePath.replace(/\.webp$/, ".png")
      const pngBaseUrl = `${assetsUrl}/${imagesBasePath}/${upperModel}/${pngPath}`
      return [
        preloadImage(`${baseUrl}${cacheBustVersion}`),
        preloadImage(`${pngBaseUrl}${cacheBustVersion}`),
      ]
    })

    Promise.all(promises).then(() => {
      if (!cancelled) setReady(true)
    })

    return () => {
      cancelled = true
    }
  }, [imagesBasePath, model])

  return ready
}

export default usePreloadImages
